import { createSelector, createSlice } from '@reduxjs/toolkit';

import { AuthUser, UserModel } from '@/models';
import { SlicePayload } from '@/types';
import { RootState } from '@/redux/create-store';

type AuthStateStatusValue = { success: true, redirectUrl?: string } | { error: any } | Record<string, never>;
type AuthStateStatusKey = 'signup' | 'signin' | 'reset' | 'change';
type AuthStateStatus = { [key in AuthStateStatusKey]?: AuthStateStatusValue };

export type AuthState = {
  loggedUser?: AuthUser;
  status: AuthStateStatus;
}

export const initialState: AuthState = {
  loggedUser: undefined,
  status: {}
};

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthStatus: (state, action: SlicePayload<{ name: AuthStateStatusKey }>) => {
      state.status[action.payload.name] = {};
    },

    // USER CREATION

    createUser: (state, _action: SlicePayload<{ user: UserModel, redirectUrl?: string }>) => {
      state.loggedUser = undefined;
      state.status.signup = {};
    },

    createUserSuccess: (state, action: SlicePayload<{ user: AuthUser }>) => {
      state.loggedUser = action.payload.user;
      state.status.signup = { success: true };
    },

    createUserError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.signup = action.payload;
    },

    // USER CONNECTION

    connectUser: (state, _action: SlicePayload<{ email: string, password: string, redirectUrl?: string }>) => {
      state.loggedUser = undefined;
      state.status.signin = {};
    },

    connectSocialNetworkUser: (state, _action: SlicePayload<{ socialNetworkInformation: object }>) => {
      state.loggedUser = undefined;
      state.status.signin = {};
    },

    connectUserSuccess: (state, action: SlicePayload<{ user: AuthUser, redirectUrl?: string }>) => {
      state.loggedUser = action.payload.user;
      state.status.signin = { success: true, redirectUrl: action.payload.redirectUrl };
    },

    connectUserError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.signin = action.payload;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    disconnectUser: () => {
    },

    disconnectUserSuccess: (state) => {
      state.loggedUser = undefined;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    connectedUser: () => {
    },

    connectedUserSuccess: (state, action: SlicePayload<{ user: AuthUser }>) => {
      state.loggedUser = action.payload.user;
    },

    connectedUserError: (state) => {
      state.loggedUser = undefined;
    },

    // USER PASSWORD RESET

    resetPassword: (state, _action: SlicePayload<{ email: string }>) => {
      state.status.reset = {};
    },

    resetPasswordSuccess: (state) => {
      state.status.reset = { success: true };
    },

    resetPasswordError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.reset = action.payload;
    },

    changePassword: (state, _action: SlicePayload<{ password: string, token: string }>) => {
      state.status.change = {};
    },

    changePasswordSuccess: (state) => {
      state.status.change = { success: true };
    },

    changePasswordError: (state, action: SlicePayload<{ error: any }>) => {
      state.status.change = action.payload;
    }
  }
});

export const {
  clearAuthStatus,
  createUser, createUserSuccess, createUserError,
  connectUser, connectSocialNetworkUser, connectUserSuccess, connectUserError,
  disconnectUser, disconnectUserSuccess,
  connectedUser, connectedUserSuccess, connectedUserError,
  resetPassword, resetPasswordSuccess, resetPasswordError,
  changePassword, changePasswordSuccess, changePasswordError
} = auth.actions;

export default auth.reducer;

const selectAuthState = (state: RootState) => state.auth;

export const selectIsUserConnected = createSelector(
  [selectAuthState],
  (auth) => auth.loggedUser !== undefined
);
